/* eslint-disable prefer-promise-reject-errors */
import { useMutation } from "@apollo/client";
import {
  Button,
  Form,
  Input,
  message,
  Popover,
  Select,
  Radio,
  Tooltip,
  Drawer,
  Col,
  Row,
} from "antd";
import { capitalize, isEmpty } from "lodash";
import { LuCirclePlus, LuMail, LuMapPin, LuPencil, LuPhone } from "react-icons/lu";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { useDispatch, useSelector } from "react-redux";
import omit from "lodash/omit";
import { UserContext } from "../../../amplify/authenticator-provider";

import {
  CREATE_LEAD_ADDRESS,
  UPDATE_LEAD_ADDRESS,
  UPDATE_LEAD_PREFFREDLANGUAGE,
  UPDATE_LEAD_PHONE_LIST,
  UPDATE_LEAD_EMAIL_LIST,
} from "../../../graphql/mutation";
import useCrmIntegration from "../../../hooks/useCrmIntegration";
import useLang from "../../../hooks/useLang";
import { parseAddress } from "../../../library/utils";
import { EmailForm } from "./EmailForm";
import { PhoneFormJsx } from "./PhoneFormJsx";
import LeadSummaryOpenAi from "./leadSummaryOpenAi";
import {
  createLeadAddressRedux,
  updateLeadAddressRedux,
  updateLeadInList,
} from "../../../redux/slices/leadSlice";

export const AboutThisLead = ({
  setSelectedLead,
  getMessages,
  leadList,
  setLeadList,
  languages,
}) => {
  const dispatch = useDispatch();
  const { selectedLead } = useSelector(state => state.lead);
  const { user } = useContext(UserContext);
  const phoneRef = useRef(null);
  const addressRef = useRef(null);
  const newEmail = useRef();
  const [lang] = useLang();
  const crmIntegration = useCrmIntegration();
  const phoneArr = ["Mobile", "Work", "Home", "Other"];

  const [primaryPhoneTypeId, setPrimaryPhoneTypeId] = useState();
  const [primaryEmailTypeId, setPrimaryEmailTypeId] = useState();
  const [phoneDrawerVisible, setPhoneDrawerVisible] = useState(false);
  const [emailDrawerVisible, setEmailDrawerVisible] = useState(false);

  const [updateLeadPreferredLanguage] = useMutation(UPDATE_LEAD_PREFFREDLANGUAGE);
  const [updateLeadPhone] = useMutation(UPDATE_LEAD_PHONE_LIST);
  const [updateLeadEmail] = useMutation(UPDATE_LEAD_EMAIL_LIST);

  useEffect(() => {
    const primaryPhones = (selectedLead?.phoneNumbers || []).filter(el => el.isPrimary);
    setPrimaryPhoneTypeId(primaryPhones.length > 0 ? primaryPhones[0].id : undefined);
  }, [selectedLead?.phoneNumbers]);

  useEffect(() => {
    const primaryEmails = (selectedLead?.emails || []).filter(el => el?.isPrimary);
    setPrimaryEmailTypeId(primaryEmails.length > 0 ? primaryEmails[0].id : undefined);
  }, [selectedLead?.emails]);

  const [hoverEdit, sethoverEdit] = useState({
    phone: false,
    email: false,
    address: false,
  });
  const address = useMemo(() => {
    return (
      selectedLead?.addresses &&
      (selectedLead?.addresses || []).length > 0 &&
      selectedLead.addresses[0]
    );
  }, [selectedLead]);

  const addressString = parseAddress(address);

  const leadPhones = (selectedLead?.phoneNumbers || [])?.map(item => ({
    ...item,
    phoneType: capitalize(item.phoneType),
    id: String(item.id),
  }));

  return (
    <div className="flex-col-centered gap-2">
      <div className="flex-col-centered gap-3">
        {/* Lead Phone Numbers */}
        <div className="w-full">
          <div className="flex items-center gap-2 mb-2">
            <LuPhone size={14} strokeWidth={1.75} className="icon-action-color" />
            <span className="text-sm font-semibold text-gray-700">Phone Information</span>
            {(leadPhones || []).length > 0 && (
              <EditPhoneIcon
                phoneRef={phoneRef}
                selectedLead={selectedLead}
                setSelectedLead={setSelectedLead}
                phoneArr={phoneArr}
                hoverEdit={{ phone: true }}
                setPhoneDrawerVisible={setPhoneDrawerVisible}
              />
            )}
          </div>
          <div
            // onMouseEnter={() => sethoverEdit({ phone: true })}
            // onMouseLeave={() => sethoverEdit({ phone: false })}
            className="w-full"
          >
            {(leadPhones || []).length > 0 ? (
              <Radio.Group
                value={String(primaryPhoneTypeId)}
                onChange={async e => {
                  setPrimaryPhoneTypeId(e.target.value);
                  const res = await updateLeadPhone({
                    variables: {
                      phones: selectedLead?.phoneNumbers.map(item => {
                        const phoneObj = omit(item, "potentialLeads");
                        return {
                          ...phoneObj,
                          isPrimary: +e?.target?.value === +item?.id,
                          phoneType: capitalize(item.phoneType),
                        };
                      }),
                      leadId: selectedLead.id,
                    },
                  });
                  if (res.data.updateLead.statusCode === 200) {
                    const leadPhonesUpdated = (res?.data?.updateLead?.lead?.phoneNumbers || []).map(
                      item => ({
                        ...item,
                        phoneType: capitalize(item.phoneType),
                      })
                    );
                    setSelectedLead({ ...selectedLead, phoneNumbers: leadPhonesUpdated });
                    message.success(res.data.updateLead.message);
                    dispatch(
                      updateLeadInList({ ...selectedLead, phoneNumbers: leadPhonesUpdated })
                    );
                  } else {
                    message.error(res.data.updateLead.message);
                  }
                }}
                className="w-full"
              >
                <div className="w-full space-y-1.5">
                  {(leadPhones || [])
                    .sort((a, b) => {
                      // "Self" first, then others
                      const relA = (a.relationshipType || "Self").toLowerCase();
                      const relB = (b.relationshipType || "Self").toLowerCase();
                      if (relA === "self" && relB !== "self") return -1;
                      if (relA !== "self" && relB === "self") return 1;
                      return 0;
                    })
                    .map((item, index) => {
                      const hasAdditionalInfo = item.firstName || item.lastName || item.note;
                      const tooltipContent = hasAdditionalInfo && (
                        <div className="space-y-1 text-xs">
                          <div className="font-semibold text-white">Phone Details:</div>
                          {(item.firstName || item.lastName) && (
                            <div>
                              <span className="text-gray-200">Contact:</span>{" "}
                              <span className="text-white">
                                {[item.firstName, item.lastName].filter(Boolean).join(" ")}
                              </span>
                            </div>
                          )}
                          {item.note && (
                            <div>
                              <span className="text-gray-200">Note:</span>{" "}
                              <span className="text-white">{item.note}</span>
                            </div>
                          )}
                        </div>
                      );

                      return (
                        <div className="flex items-center w-full py-1" key={index}>
                          <Tooltip title="Primary" placement="right" mouseEnterDelay={0.3}>
                            <Radio value={String(item?.id)} className="flex-shrink-0" />
                          </Tooltip>
                          <div className="flex-1">
                            <Tooltip title={tooltipContent} placement="top" mouseEnterDelay={0.3}>
                              <div className="flex items-center gap-3 border border-gray-100">
                                <div className="flex flex-col flex-1 min-w-0">
                                  <span className="font-semibold text-gray-600 text-sm break-all">
                                    {formatPhoneNumberIntl(item.phone)}
                                    <span className="text-gray-400 text-sm font-normal ml-1">
                                      ({capitalize(item.phoneType)})
                                    </span>
                                  </span>
                                  {item.relationshipType && item.relationshipType !== "Self" && (
                                    <div className="flex gap-2 mt-1 flex-wrap">
                                      {item.firstName && (
                                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-normal text-gray-400 ">
                                          {capitalize(item.firstName)}{" "}
                                          {item.lastName && ` ${capitalize(item.lastName)}`}
                                        </span>
                                      )}
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700 font-medium">
                                        {capitalize(item.relationshipType)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </Tooltip>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </Radio.Group>
            ) : (
              <span
                ref={phoneRef}
                className="flex items-center cursor-pointer text-brand-500 gap-1"
                onClick={() => setPhoneDrawerVisible(true)}
              >
                <LuCirclePlus size={14} strokeWidth={1.75} /> Add phone
              </span>
            )}
          </div>
        </div>
        {/* Lead Phone Numbers */}
        {/* Lead Email ids */}
        <div className="w-full">
          <div className="flex items-center gap-2 mb-2">
            <LuMail size={14} strokeWidth={1.75} className="icon-action-color" />
            <span className="text-sm font-semibold text-gray-700">Email Information</span>
            {(selectedLead?.emails || []).length > 0 && (
              <EditEmailIcon
                newEmail={newEmail}
                setSelectedLead={setSelectedLead}
                selectedLead={selectedLead}
                hoverEdit={{ email: true }}
                setEmailDrawerVisible={setEmailDrawerVisible}
              />
            )}
          </div>
          <div
            // onMouseEnter={() => sethoverEdit({ email: true })}
            // onMouseLeave={() => sethoverEdit({ email: false })}
            className="w-full"
          >
            {(selectedLead?.emails || []).filter(item => item?.email)?.length > 0 ? (
              <Radio.Group
                value={String(primaryEmailTypeId)}
                onChange={async e => {
                  setPrimaryEmailTypeId(e.target.value);
                  const res = await updateLeadEmail({
                    variables: {
                      emails: selectedLead?.emails.map(item => {
                        return {
                          ...item,
                          isPrimary: +e?.target?.value === +item?.id,
                        };
                      }),
                      leadId: selectedLead.id,
                    },
                  });
                  if (res.data.updateLead.statusCode === 200) {
                    const updatedEmails = (res?.data?.updateLead?.lead?.emails || []).map(
                      email => ({
                        ...email,
                        emailType: capitalize(email.emailType),
                      })
                    );
                    setSelectedLead({ ...selectedLead, emails: updatedEmails });
                    message.success(res.data.updateLead.message);
                    dispatch(
                      updateLeadInList({
                        ...selectedLead,
                        emails: updatedEmails,
                      })
                    );
                  } else {
                    message.error(res.data.updateLead.message);
                  }
                }}
                className="w-full"
              >
                <div className="w-full space-y-1.5">
                  {(selectedLead?.emails || [])
                    .filter(item => item.email)
                    .sort((a, b) => {
                      // "Self" first, then others
                      const relA = (a.relationshipType || "Self").toLowerCase();
                      const relB = (b.relationshipType || "Self").toLowerCase();
                      if (relA === "self" && relB !== "self") return -1;
                      if (relA !== "self" && relB === "self") return 1;
                      return 0;
                    })
                    .map((item, index) => {
                      const hasAdditionalInfo = item.firstName || item.lastName || item.note;
                      const tooltipContent = hasAdditionalInfo && (
                        <div className="space-y-1 text-xs">
                          <div className="font-semibold text-white">Email Details:</div>
                          {(item.firstName || item.lastName) && (
                            <div>
                              <span className="text-gray-200">Contact:</span>{" "}
                              <span className="text-white">
                                {[item.firstName, item.lastName].filter(Boolean).join(" ")}
                              </span>
                            </div>
                          )}
                          {item.note && (
                            <div>
                              <span className="text-gray-200">Note:</span>{" "}
                              <span className="text-white">{item.note}</span>
                            </div>
                          )}
                        </div>
                      );

                      return (
                        <div className="flex items-center w-full py-1" key={index}>
                          <Tooltip title="Primary" placement="right" mouseEnterDelay={0.3}>
                            <Radio value={String(item?.id)} className="flex-shrink-0" />
                          </Tooltip>
                          <div className="flex-1">
                            <Tooltip title={tooltipContent} placement="top" mouseEnterDelay={0.3}>
                              <div className="flex items-center gap-3 border border-gray-100">
                                <div className="flex flex-col flex-1 min-w-0">
                                  <span className="font-medium text-gray-900 text-sm break-all">
                                    {item.email}
                                    <span className="text-gray-400 text-sm font-normal ml-1">
                                      ({capitalize(item.emailType)})
                                    </span>
                                  </span>
                                  {item.relationshipType && item.relationshipType !== "Self" && (
                                    <div className="flex gap-2 mt-1 flex-wrap">
                                      {item.firstName && (
                                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-normal text-gray-400 ">
                                          {capitalize(item.firstName)}{" "}
                                          {item.lastName && ` ${capitalize(item.lastName)}`}
                                        </span>
                                      )}
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700 font-medium">
                                        {capitalize(item.relationshipType)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </Tooltip>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </Radio.Group>
            ) : (
              <span
                ref={newEmail}
                className="flex items-center cursor-pointer text-brand-500 gap-1"
                onClick={() => setEmailDrawerVisible(true)}
              >
                <LuCirclePlus size={14} strokeWidth={1.75} /> Add Email
              </span>
            )}
          </div>
        </div>
        {/* Lead Email ids */}
        <div className="flex items-start gap-2 w-full">
          <span className="mt-1 text-gray-500">
            <LuMapPin className="icon-action-color" />
          </span>
          <div className="flex-1 cursor-pointer">
            {isEmpty(addressString) ? (
              <Popover
                content={
                  <AddressForm mode="ADD" selectedLead={selectedLead} addressRef={addressRef} />
                }
                trigger={["click"]}
              >
                <div className="flex-row-centered text-brand-500 gap-1" ref={addressRef}>
                  <LuCirclePlus size={14} strokeWidth={1.75} className="text-twLinkColor" />
                  <span>Add Address</span>
                </div>
              </Popover>
            ) : (
              <Popover
                content={
                  <AddressForm
                    mode="EDIT"
                    selectedLead={selectedLead}
                    address={address}
                    addressRef={addressRef}
                  />
                }
                trigger={["click"]}
              >
                <div
                  onMouseEnter={() => sethoverEdit({ address: true })}
                  onMouseLeave={() => sethoverEdit({ address: false })}
                  ref={addressRef}
                >
                  {addressString}
                  <LuPencil
                    className={`cursor-pointer text-twLinkColor ${
                      hoverEdit.address ? "visible" : "invisible"
                    }`}
                  />
                </div>
              </Popover>
            )}
          </div>
        </div>
      </div>
      <div className="flex-row-centered items-center justify-between">
        <span>{lang.preferredlang}</span>
        <Select
          className="w-[100px]"
          onChange={value => {
            updateLeadPreferredLanguage({
              variables: {
                leadId: selectedLead?.id,
                preferredLanguageCode: value,
              },
              onCompleted: res => {
                if (res?.updateLeadPreferredLanguage?.statusCode === 200) {
                  getMessages();
                  const arrayFn1 = () => {
                    const clone = structuredClone(selectedLead);
                    clone.preferredLanguageCode =
                      res?.updateLeadPreferredLanguage?.preferredLanguageCode;
                    return clone;
                  };
                  setSelectedLead(arrayFn1());
                  const arrayFn = () => {
                    const clone = structuredClone(leadList);
                    const newLeadList = clone?.map(item => {
                      if (+selectedLead?.id === +item?.id) {
                        return {
                          ...item,
                          preferredLanguageCode:
                            res?.updateLeadPreferredLanguage?.preferredLanguageCode,
                        };
                      }
                      return item;
                    });
                    return newLeadList;
                  };
                  setLeadList(arrayFn());
                }
              },
            });
          }}
          value={selectedLead?.preferredLanguageCode}
          placeholder={lang.preferredlang}
        >
          {(languages || []).map(item => (
            <Select.Option key={item?.id} value={item?.languageCode}>
              {item.languageName}
            </Select.Option>
          ))}
        </Select>
      </div>
      <div className="flex-row-centered items-center justify-between">
        <LeadSummaryOpenAi
          key={selectedLead?.id}
          selectedLead={selectedLead}
          user={user}
          text="Get Overview"
          labelText="Otto 360 Overview"
        />
      </div>

      {/* Phone Drawer */}
      <Drawer
        title="Edit Phone Numbers"
        placement="right"
        width={800}
        onClose={() => setPhoneDrawerVisible(false)}
        open={phoneDrawerVisible}
        destroyOnClose
      >
        <PhoneFormJsx
          phoneRef={phoneRef}
          phones={
            Array.isArray(selectedLead?.phoneNumbers)
              ? (selectedLead.phoneNumbers || []).map(item => ({
                  ...item,
                  phoneType: item?.phoneType ? capitalize(item.phoneType) : "",
                }))
              : []
          }
          selectedLead={selectedLead}
          setSelectedLead={setSelectedLead}
          phoneArr={phoneArr}
          onClose={() => setPhoneDrawerVisible(false)}
        />
      </Drawer>

      {/* Email Drawer */}
      <Drawer
        title="Edit Email Addresses"
        placement="right"
        width={800}
        onClose={() => setEmailDrawerVisible(false)}
        open={emailDrawerVisible}
        destroyOnClose
      >
        <EmailForm
          emailRef={newEmail}
          setSelectedLead={setSelectedLead}
          emails={selectedLead?.emails}
          onClose={() => setEmailDrawerVisible(false)}
        />
      </Drawer>
    </div>
  );
};
function EditEmailIcon({
  newEmail,
  setSelectedLead,
  selectedLead,
  hoverEdit,
  setEmailDrawerVisible,
}) {
  return (
    <span
      className="cursor-pointer"
      onClick={e => {
        e.stopPropagation();
        setEmailDrawerVisible(true);
      }}
      ref={newEmail}
    >
      <LuPencil
        className={`cursor-pointer text-twLinkColor ${hoverEdit?.email ? "visible" : "invisible"}`}
      />
    </span>
  );
}

function EditPhoneIcon({
  phoneRef,
  selectedLead,
  setSelectedLead,
  phoneArr,
  hoverEdit,
  setPhoneDrawerVisible,
}) {
  return (
    <span
      className="cursor-pointer"
      onClick={e => {
        e.stopPropagation();
        setPhoneDrawerVisible(true);
      }}
      ref={phoneRef}
    >
      <LuPencil
        className={`cursor-pointer text-twLinkColor ${hoverEdit.phone ? "visible" : "invisible"}`}
      />
    </span>
  );
}

const AddressForm = props => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { address, addressRef, selectedLead, mode } = props;
  const close = () => addressRef.current.click();
  useEffect(() => {
    form.setFieldsValue(address);
  }, [address]);
  const [updateLeadAddress, { loading: updateleadAddressLoader }] =
    useMutation(UPDATE_LEAD_ADDRESS);
  const [createLeadAddress, { loading: createLeadAddressLoader }] =
    useMutation(CREATE_LEAD_ADDRESS);
  const handleSubmit = async value => {
    if (mode === "EDIT") {
      const res = await updateLeadAddress({
        variables: {
          id: address.id,
          ...value,
        },
      });
      if (res.data.updateLeadAddress.statusCode === 200) {
        message.success(res.data.updateLeadAddress.message);
        dispatch(updateLeadAddressRedux(res.data.updateLeadAddress.leadAddress));
        close();
      } else {
        message.error(res.data.updateLeadAddress.message);
      }
    } else {
      const res = await createLeadAddress({
        variables: {
          leadId: selectedLead.id,
          ...value,
        },
      });
      if (res?.data?.createLeadAddress?.statusCode === 200) {
        message.success(res?.data?.createLeadAddress?.message);
        dispatch(createLeadAddressRedux(res?.data?.createLeadAddress?.leadAddress));
        close();
      }
    }
  };
  return (
    <Form layout="vertical" form={form} onFinish={value => handleSubmit(value)}>
      <div className="flex-row-centered gap-1">
        <Form.Item
          className="w-full"
          name="addressLine1"
          // rules={[{ required: true, message: "Primary Address is required" }]}
          label="Address Line 1"
        >
          <Input name="addressLine1" placeholder="Enter Address Line 1" />
        </Form.Item>
        <Form.Item className="w-full" name="addressLine2" label="Address Line 2">
          <Input name="addressLine2" placeholder="Enter Address Line 2" />
        </Form.Item>
      </div>
      <div className="flex-row-centered gap-1">
        <Form.Item
          className="w-full"
          name="city"
          // rules={[{ required: true, message: "City is required" }]}
          label="City"
        >
          <Input name="city" placeholder="Enter City" />
        </Form.Item>
        <Form.Item
          className="w-full"
          name="state"
          // rules={[{ required: true, message: "State is required" }]}
          label="State"
        >
          <Input name="state" placeholder="Enter State" />
        </Form.Item>
      </div>
      <div className="flex-row-centered gap-1">
        <Form.Item
          className="w-full"
          name="postalCode"
          // rules={[{ required: true, message: "Postal Code is required" }]}
          label="Postal Code"
        >
          <Input type="number" name="postalCode" placeholder="Enter Postal Code" />
        </Form.Item>
        <Form.Item
          className="w-full"
          name="country"
          // rules={[{ required: true, message: "Country is required" }]}
          label="Country"
        >
          <Input name="country" placeholder="Enter Country" />
        </Form.Item>
      </div>
      <div className="flex-row-centered gap-1 justify-start">
        <Button
          loading={createLeadAddressLoader || updateleadAddressLoader}
          type="primary"
          htmlType="submit"
        >
          Save
        </Button>
        <Button onClick={close}>Cancel</Button>
      </div>
    </Form>
  );
};
